/**
 * EQuIP Leadership Wrapper Page
 *
 * Wrapper page for the leadership dashboard.
 * Lives in pages/ (not views/equip/) so it is NOT overwritten by update.sh.
 *
 * Adds the shared site chrome — FloatingNav, HeroSection, max-w-7xl container —
 * then renders LeadershipProgressDetails from the synced views/equip/ directory.
 *
 * FloatingNav role logic mirrors EquipDashboardPage:
 * - Physician lead / admin  → rolePrefix="physician"
 * - APC lead               → rolePrefix="non-physician"
 */

import { Calculator, BookOpen, BarChart3 } from 'lucide-react'
import { FloatingNav } from '@/components/layout/FloatingNav'
import { HeroSection } from '@/components/layout/HeroSection'
import { useRBAC } from '@/hooks/use-rbac'
import { ROLES } from '@/lib/roles'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'
import { LeadershipProgressDetails } from '@/views/equip/leadership/LeadershipProgressDetails'

export function EquipLeadershipWrapper() {
  const { isAdmin, hasRole } = useRBAC()
  const scope = useEquipScopeOptional()

  const navRolePrefix: 'physician' | 'non-physician' =
    isAdmin || hasRole(ROLES.EQUIP_PHYSICIAN_LEAD) ? 'physician' : 'non-physician'

  const navItems = [
    { id: 'framework', label: 'Framework', path: `/${navRolePrefix}/framework`, icon: <Calculator className="w-4 h-4" /> },
    { id: 'leader-guide', label: 'Leader Guide', path: `/${navRolePrefix}/leader-guide`, icon: <BookOpen className="w-4 h-4" /> },
    { id: 'faq', label: 'FAQ', path: `/${navRolePrefix}/faq` },
    { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership', icon: <BarChart3 className="w-4 h-4" /> },
  ]

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      <FloatingNav items={navItems} showHome />

      <HeroSection
        role={navRolePrefix}
        badgeText="Leadership Dashboard"
        title="Team Performance"
        description="Monitor your team's EQuIP quality measures, outcomes, and award progress."
      />

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <LeadershipProgressDetails managerEmail={scope?.selectedManager ?? null} />
        </div>
      </section>
    </div>
  )
}
